// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setOrderReason from "@store/Framework/Store/Pages/Cart/Handlers/SetOrderReason";
import { OrderReasonsModel } from "@store/Framework/Types/ApiModels";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        cart: getCurrentCartState(state).value,
        orderReason: state.store.pages.cart.orderReason,
    };
};

const mapDispatchToProps = {
    setOrderReason,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartOrderReasonStyles {
    wrapper?: InjectableCss;
    specificReason?: InjectableCss;
}

export const cartOrderReasonStyles: CartOrderReasonStyles = {
    wrapper: {
        css: css`
            padding: ${pxToRem(20, 0)};
            border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};
        `,
    },
    specificReason: {
        css: css`
            margin-top: ${pxToRem(15)};
        `,
    },
};

const styles = cartOrderReasonStyles;

const CartOrderReason: FC<Props> = ({ cart, orderReason, setOrderReason }) => {
    const [availableReasons, setAvailableReasons] = useState<OrderReasonsModel[]>([]);
    const [selectedReasonCode, setSelectedReasonCode] = useState<OrderReasonsModel>();
    const [canInputSpecificReason, setCanInputSpecificReason] = useState(false);
    const [canShowOrderReason, setCanShowOrderReason] = useState(false);

    useEffect(() => {
        if (orderReason) {
            setSelectedReasonCode(orderReason);
        }
    }, []);

    useEffect(() => {
        if (cart && cart.properties.availableOrderReasons) {
            setAvailableReasons(JSON.parse(cart.properties.availableOrderReasons));
        }
    }, [cart]);

    useEffect(() => {
        if (availableReasons.length > 0) {
            setCanShowOrderReason(true);
        }
    }, [availableReasons]);

    useEffect(() => {
        if (selectedReasonCode) {
            setCanInputSpecificReason(selectedReasonCode.canInputSpecificReason);
            setOrderReason({ orderReason: selectedReasonCode });
        }
    }, [selectedReasonCode]);

    if (!canShowOrderReason) {
        return <></>;
    }

    const reasonChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedReasonCode = availableReasons.find(reason => reason.code === event.currentTarget.value);
        setSelectedReasonCode(selectedReasonCode);
    };

    const specificReasonChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        if (selectedReasonCode) {
            const reason = cloneDeep(selectedReasonCode);
            reason.specificReason = value;
            setSelectedReasonCode(reason);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Select onChange={reasonChangeHandler} value={selectedReasonCode?.code}>
                <option value="">{translate("Select an Order Reason")}</option>
                {availableReasons.map(reason => (
                    <option key={reason.code} value={reason.code}>
                        {reason.description}
                    </option>
                ))}
            </Select>
            {canInputSpecificReason && (
                <TextField
                    {...styles.specificReason}
                    placeholder={translate("Other Reason")}
                    onChange={specificReasonChangeHandler}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartOrderReason),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        displayName: "Order Reason",
    },
};

export default widgetModule;
